import { AxiosError, HttpStatusCode } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fAPIDate } from 'src/utils/format-time';

import {
  ChequeStatus,
  IChequeItem,
  IChequeListItem,
  IChequeReceived,
  IChequePayment,
  IChequeSettlement,
  IChequeSettlementSummary,
  IChequeTimeline,
} from 'src/types/cheque';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  cheque: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IChequeListItem[];
  };
  total: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: { totalAmount: number | null; pendingWithdrawal: number | null };
  };
  item: IChequeItem | null;
  received: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: IChequeReceived[];
  };
  payments: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    items: IChequePayment[];
    total: number;
  };
  bankOptions: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: string[];
  };
  receivedViaOptions: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    data: string[];
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  cheque: {
    count: 0,
    page: 1,
    pageSize: 10,
    pageCount: 0,
    items: [],
  },
  total: {
    status: 'idle',
    error: undefined,
    data: { totalAmount: null, pendingWithdrawal: null },
  },
  item: null,
  received: {
    status: 'idle',
    error: undefined,
    data: [],
  },
  payments: {
    status: 'idle',
    error: undefined,
    items: [],
    total: 0,
  },
  bankOptions: {
    status: 'idle',
    error: undefined,
    data: [],
  },
  receivedViaOptions: {
    status: 'idle',
    error: undefined,
    data: [],
  },
};

const cheque = createSlice({
  name: 'cheque',
  initialState,
  reducers: {
    removeChequeItem: (state, action) => {
      const { itemId } = action.payload;

      const index = state.cheque.items.findIndex((item) => item._id === itemId);

      if (index > -1) {
        action.payload.index = index; // Store the index for rollback
        state.cheque.items = state.cheque.items.filter((item) => item._id !== itemId);
      }
    },
    rollbackChequeItemDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.cheque.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCheques.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getCheques.fulfilled, (state, action) => {
        state.status = 'success';
        state.cheque = action.payload;
      })
      .addCase(getCheques.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getChequeReceived.pending, (state) => {
        state.received.status = 'loading';
      })
      .addCase(getChequeReceived.fulfilled, (state, action) => {
        state.received.status = 'success';
        state.received.data = action.payload;
      })
      .addCase(getChequeReceived.rejected, (state, action) => {
        state.received.status = 'failure';
        state.received.error = action.error.message;
      })

      .addCase(getChequeTotal.pending, (state) => {
        state.total.status = 'loading';
      })
      .addCase(getChequeTotal.fulfilled, (state, action) => {
        state.total.status = 'success';
        state.total.data = action.payload;
      })
      .addCase(getChequeTotal.rejected, (state, action) => {
        state.total.status = 'failure';
        state.total.error = action.error.message;
      })

      .addCase(getChequeByID.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getChequeByID.fulfilled, (state, action) => {
        state.status = 'success';
        state.item = action.payload;
      })
      .addCase(getChequeByID.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getChequePayments.pending, (state) => {
        state.payments.status = 'loading';
      })
      .addCase(getChequePayments.fulfilled, (state, action) => {
        state.payments.status = 'success';
        state.payments.items = action.payload;
      })
      .addCase(getChequePayments.rejected, (state, action) => {
        state.payments.status = 'failure';
        state.payments.error = action.error.message;
      })

      .addCase(getChequePaymentTotal.pending, (state) => {
        state.payments.status = 'loading';
      })
      .addCase(getChequePaymentTotal.fulfilled, (state, action) => {
        state.payments.status = 'success';
        state.payments.total = action.payload.totalPaid;
      })
      .addCase(getChequePaymentTotal.rejected, (state, action) => {
        state.payments.status = 'failure';
        state.payments.error = action.error.message;
      })

      .addCase(createChequePayment.pending, (state) => {
        state.payments.status = 'loading';
      })
      .addCase(createChequePayment.fulfilled, (state, action) => {
        state.payments.status = 'success';
        state.payments.items.push(action.payload);
      })
      .addCase(createChequePayment.rejected, (state, action) => {
        state.payments.status = 'failure';
        state.payments.error = action.error.message;
      })

      .addCase(getChequeBankOptions.pending, (state) => {
        state.bankOptions.status = 'loading';
      })
      .addCase(getChequeBankOptions.fulfilled, (state, action) => {
        state.bankOptions.status = 'success';
        state.bankOptions.data = action.payload;
      })
      .addCase(getChequeBankOptions.rejected, (state, action) => {
        state.bankOptions.status = 'failure';
        state.bankOptions.error = action.error.message;
      })

      .addCase(getChequeReceivedViaOptions.pending, (state) => {
        state.receivedViaOptions.status = 'loading';
      })
      .addCase(getChequeReceivedViaOptions.fulfilled, (state, action) => {
        state.receivedViaOptions.status = 'success';
        state.receivedViaOptions.data = action.payload;
      })
      .addCase(getChequeReceivedViaOptions.rejected, (state, action) => {
        state.receivedViaOptions.status = 'failure';
        state.receivedViaOptions.error = action.error.message;
      });
  },
});

export default cheque.reducer;

export const { removeChequeItem, rollbackChequeItemDeletion } = cheque.actions;

export const getCheques = createAsyncThunk(
  'cheque/getCheques',
  async (
    params: {
      page: number;
      limit: number;
      generalSearch?: string | null;
      accountNumber?: string | null;
      customer?: string | null;
      chequeNumber?: string | null;
      receivedVia?: string | null;
      holder?: string | null;
      chequeDate?: Date | null;
      startDate?: Date | null;
      endDate?: Date | null;
      receivedDate?: Date | null;
      paidDate?: Date | null;
      bankedDate?: Date | null;
      chequeDateUpTo?: Date | null;
      bank?: string | null;
      status?: string | null; // TODO: Enum
      type?: string | null; // TODO: Enum
      isFactored?: boolean | null;
      factoringStatus?: string | null;
      sortBy?: string | null;
      sort?: 'asc' | 'desc' | null;
    } = {
      page: 1,
      limit: 10,
      generalSearch: null,
      accountNumber: null,
      customer: null,
      chequeNumber: null,
      receivedVia: null,
      bank: null,
      chequeDate: null,
      startDate: null,
      endDate: null,
      receivedDate: null,
      paidDate: null,
      holder: null,
      status: null,
      type: null,
      isFactored: null,
      factoringStatus: null,
      sort: 'desc',
      sortBy: 'updatedAt',
    }
  ) => {
    const {
      page,
      limit,
      generalSearch,
      accountNumber,
      sortBy,
      sort,
      bank,
      chequeDate,
      chequeNumber,
      customer,
      holder,
      receivedVia,
      status,
      type,
      isFactored,
      factoringStatus,
      startDate,
      endDate,
      receivedDate,
      paidDate,
      bankedDate,
      chequeDateUpTo,
    } = params;

    try {
      const response = await axios.get(
        `cheque?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&accountNumber=${accountNumber ?? ''}&customer=${customer ?? ''}&chequeNumber=${
          chequeNumber ?? ''
        }&receivedVia=${receivedVia ?? ''}&holder=${holder ?? ''}&chequeDate=${
          chequeDate ?? ''
        }&receivedDate=${receivedDate ?? ''}&paidDate=${paidDate ?? ''}&bank=${bank ?? ''}&status=${
          status ?? ''
        }&type=${type ?? ''}&isFactored=${isFactored ?? ''}&factoringStatus=${
          factoringStatus ?? ''
        }&sort=${sort ?? ''}&sortBy=${sortBy}&startDate=${fAPIDate(startDate)}&endDate=${fAPIDate(
          endDate
        )}&chequeDateUpTo=${fAPIDate(chequeDateUpTo)}&bankedDate=${fAPIDate(bankedDate)}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getChequeReceived = createAsyncThunk('cheque/getChequeReceived', async () => {
  try {
    const response = await axios.get(`cheque/received`);

    return response.data.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
});

export const getChequeTotal = createAsyncThunk(
  'cheque/getTotal',
  async (
    params: {
      generalSearch?: string | null;
      accountNumber?: string | null;
      customer?: string | null;
      chequeNumber?: string | null;
      receivedVia?: string | null;
      holder?: string | null;
      chequeDate?: Date | null;
      startDate?: Date | null;
      endDate?: Date | null;
      receivedDate?: Date | null;
      paidDate?: Date | null;
      bankedDate?: Date | null;
      chequeDateUpTo?: Date | null;
      bank?: string | null;
      status?: string | null;
      type?: string | null;
      isFactored?: boolean | null;
      factoringStatus?: string | null;
    } = {
      generalSearch: null,
      accountNumber: null,
      customer: null,
      chequeNumber: null,
      receivedVia: null,
      bank: null,
      chequeDate: null,
      startDate: null,
      endDate: null,
      holder: null,
      status: null,
      type: null,
      isFactored: null,
      factoringStatus: null,
      receivedDate: null,
      paidDate: null,
    }
  ) => {
    const {
      generalSearch,
      accountNumber,
      bank,
      chequeDate,
      chequeNumber,
      customer,
      holder,
      receivedVia,
      status,
      type,
      isFactored,
      factoringStatus,
      startDate,
      endDate,
      receivedDate,
      paidDate,
      bankedDate,
      chequeDateUpTo,
    } = params;

    try {
      const response = await axios.get(
        `cheque/total?generalSearch=${generalSearch ?? ''}&accountNumber=${
          accountNumber ?? ''
        }&customer=${customer ?? ''}&chequeNumber=${chequeNumber ?? ''}&receivedVia=${
          receivedVia ?? ''
        }&holder=${holder ?? ''}&chequeDate=${chequeDate ?? ''}&receivedDate=${
          receivedDate ?? ''
        }&paidDate=${paidDate ?? ''}&bank=${bank ?? ''}&status=${status ?? ''}&type=${
          type ?? ''
        }&isFactored=${isFactored ?? ''}&factoringStatus=${
          factoringStatus ?? ''
        }&startDate=${fAPIDate(startDate)}&endDate=${fAPIDate(endDate)}&chequeDateUpTo=${fAPIDate(
          chequeDateUpTo
        )}&bankedDate=${fAPIDate(bankedDate)}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getChequeByID = createAsyncThunk(
  'cheque/getChequeByID',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`cheque/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteCheque = createAsyncThunk(
  'cheque/deleteCheque',
  async (params: { item: IChequeListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`cheque/${item._id}`);
      return response.data.data;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.FailedDependency) {
        dispatch(rollbackChequeItemDeletion({ item, index }));
        throw new AxiosError(err.response.data.message);
      }

      throw err;
    }
  }
);

export interface TCreateChequeItem
  extends Pick<
    IChequeItem,
    | 'customer'
    | 'accountNumber'
    | 'branchCode'
    | 'receivedVia'
    | 'chequeNumber'
    | 'amountInCents'
    | 'bank'
    | 'status'
    | 'payerAccountNumber'
  > {
  chequeDate: Date;
  receivedDate: Date;
  status: ChequeStatus;
  invoice?: string | null;
  payerName?: string | null;
  note?: string | null;
}

export interface TStatusUpdateChequeItem {
  status: ChequeStatus;
  holder?: string | null;
  date: Date;
}

export const createCheque = createAsyncThunk(
  'cheque/createCheque',
  async (params: { newCheque: TCreateChequeItem }) => {
    const { newCheque } = params;
    const response = await axios.post(`cheque`, newCheque);
    return response.data.data;
  }
);

export const updateCheque = createAsyncThunk(
  'cheque/updateCheque',
  async (params: { chequeID: string; updatingCheque: TCreateChequeItem }) => {
    const { updatingCheque, chequeID } = params;
    const response = await axios.patch(`cheque/${chequeID}`, updatingCheque);
    return response.data.data;
  }
);

export const updateChequeStatus = createAsyncThunk(
  'cheque/updateChequeStatus',
  async (params: { chequeID: string; updatingCheque: TStatusUpdateChequeItem }) => {
    const { updatingCheque, chequeID } = params;
    const response = await axios.patch(`cheque/${chequeID}/status`, updatingCheque);
    return response.data.data;
  }
);

export const getChequePayments = createAsyncThunk(
  'cheque/getChequePayments',
  async (params: { chequeId: string }) => {
    const { chequeId } = params;
    const response = await axios.get(`cheque/${chequeId}/payment`);
    return response.data.data;
  }
);

export const getChequePaymentTotal = createAsyncThunk(
  'cheque/getChequePaymentTotal',
  async (params: { chequeId: string }) => {
    const { chequeId } = params;
    const response = await axios.get(`cheque/${chequeId}/payment/total`);
    return response.data.data;
  }
);

export interface TCreateChequePayment {
  paymentDate: Date;
  amountInCents: number;
  note?: string;
  paidDirectToSupplier?: boolean;
}

export const createChequePayment = createAsyncThunk(
  'cheque/createChequePayment',
  async (params: { chequeId: string; payment: TCreateChequePayment }) => {
    const { chequeId, payment } = params;
    const response = await axios.post(`cheque/${chequeId}/payment`, payment);
    return response.data.data;
  }
);

export const getChequeBankOptions = createAsyncThunk('cheque/getChequeBankOptions', async () => {
  const response = await axios.get(`suggestions?type=bank`);
  return response.data.data;
});

export const getChequeReceivedViaOptions = createAsyncThunk(
  'cheque/getChequeReceivedViaOptions',
  async () => {
    const response = await axios.get(`suggestions?type=receivedVia`);
    return response.data.data;
  }
);

export const createSuggestion = createAsyncThunk(
  'cheque/createSuggestion',
  async (params: { type: 'bank' | 'receivedVia'; value: string }) => {
    const response = await axios.post(`suggestions`, params);
    return response.data.data;
  }
);

// Supplier settlements for a returned cheque — kept in component state (like
// the recovery payments' list view), so no slice state is needed.
export interface TChequeSettlementBody {
  settlementDate: Date;
  amountInCents: number;
  note?: string;
}

export const getChequeTimeline = createAsyncThunk(
  'cheque/getChequeTimeline',
  async (params: { chequeId: string }) => {
    const response = await axios.get(`cheque/${params.chequeId}/timeline`);
    return response.data.data as IChequeTimeline;
  }
);

export const getChequeSettlements = createAsyncThunk(
  'cheque/getChequeSettlements',
  async (params: { chequeId: string }) => {
    const response = await axios.get(`cheque/${params.chequeId}/settlement`);
    return response.data.data as IChequeSettlement[];
  }
);

export const getChequeSettlementSummary = createAsyncThunk(
  'cheque/getChequeSettlementSummary',
  async (params: { chequeId: string }) => {
    const response = await axios.get(`cheque/${params.chequeId}/settlement/summary`);
    return response.data.data as IChequeSettlementSummary;
  }
);

export const createChequeSettlement = createAsyncThunk(
  'cheque/createChequeSettlement',
  async (params: { chequeId: string; settlement: TChequeSettlementBody }) => {
    const response = await axios.post(`cheque/${params.chequeId}/settlement`, params.settlement);
    return response.data.data as IChequeSettlement;
  }
);

export const updateChequeSettlement = createAsyncThunk(
  'cheque/updateChequeSettlement',
  async (params: {
    chequeId: string;
    settlementId: string;
    settlement: Partial<TChequeSettlementBody>;
  }) => {
    const response = await axios.patch(
      `cheque/${params.chequeId}/settlement/${params.settlementId}`,
      params.settlement
    );
    return response.data.data as IChequeSettlement;
  }
);

export const deleteChequeSettlement = createAsyncThunk(
  'cheque/deleteChequeSettlement',
  async (params: { chequeId: string; settlementId: string }) => {
    const response = await axios.delete(
      `cheque/${params.chequeId}/settlement/${params.settlementId}`
    );
    return response.data.data;
  }
);
